import type { TextInputProps } from '@ksyos/design-system/dist/components/text-input';
import { FormGroup, TextInput } from '@ksyos/design-system';
import type { FormContextProps } from '../elements/formContext';
import type { OmittedFormGroupFieldProps } from './FormGroup';
import { getValidationErrorTranslation } from '../../utils/getValidationErrorTranslation';
import { FormContextConsumer } from '../elements/formContext';
import { computeFormGroupProps } from './FormGroup';

type OmittedTextInputProps = Omit<TextInputProps, 'onChange' | 'value'>;

export interface TextInputFieldProps extends OmittedTextInputProps {
    dontValidateOnChange?: boolean;
    onChange: (value: string | null) => void | Promise<void>;
    onDoValidate?: () => Promise<void>;
    value: string | null;
}

export function computeTextInputProps(properties: TextInputFieldProps): TextInputProps {
    const {
        disabled,
        dontValidateOnChange,
        error,
        onBlur,
        onChange,
        onDoValidate,
        onEnterKeyPress,
        onReset,
        placeholder,
        size,
        suffix,
        type,
        value,
        variant,
    } = properties;
    const validateOnChange = !dontValidateOnChange && onDoValidate;

    return {
        disabled,
        error: getValidationErrorTranslation(error),
        name: (properties as any).fieldKey,
        onBlur: (event) => {
            if (onBlur) {
                onBlur(event);
            }

            return;
        },
        onChange: async (event) => {
            const valueToChange = event.target.value === '' ? null : event.target.value;
            if (onChange) {
                await onChange(valueToChange);
            }
            if (validateOnChange) {
                await validateOnChange();
            }
        },
        onEnterKeyPress,
        onReset,
        placeholder,
        size,
        suffix,
        type,
        value: value !== null ? value : '',
        variant,
    };
}

export type TextInputFormGroupFieldProps = OmittedFormGroupFieldProps & TextInputFieldProps;

export function TextInputFormGroupField(props: FormContextProps<TextInputFormGroupFieldProps>) {
    return (
        <FormContextConsumer {...props}>
            {(properties: any) => (
                <FormGroup {...computeFormGroupProps(properties)}>
                    <TextInput {...computeTextInputProps(properties)} />
                </FormGroup>
            )}
        </FormContextConsumer>
    );
}

export type TextInputWithLabelProps = {
    label: string;
    onChange: React.ChangeEventHandler<HTMLInputElement>;
    value: string;
};

export function TextInputWithLabel({ label, onChange, value }: TextInputWithLabelProps) {
    return (
        <div style={{ width: '100%', paddingLeft: '12px' }}>
            <p style={{ marginBottom: '4px', color: 'neutral-900' }}>{label}</p>
            <TextInput
                onChange={onChange}
                value={value || ''}
            />
        </div>
    );
}
